import React, { Fragment, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import {
  Button,
  Col,
  Form,
  Icon,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
} from 'antd'
import QuestHeader from './components/QuestHeader.jsx'
import history from '../../history.js'
import _ from 'lodash'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import actions from '../../store/actions/index.js'
import './quest.scss'
import Badge from '../Courses/Create/nestedComponents/CourseCreateMain/components/Badge'
import Certificate from '../Courses/Create/nestedComponents/CourseCreateMain/components/Certificate'
import StoryModal from './components/StoryModal.jsx'
import GenerateLocationModal from './components/GenerateLocationModal.jsx'
import ContentChoiceStep from '../../components/ContentChoiceSelection/ContentChoiceSetup.jsx'
import { api } from '../../services/index.js'
import { ImageSelect } from '../../components/Form/index.js'

const { Option } = Select

const ageGroupOptions = [
  { label: 'Preschool (3-5)', value: '3-5' },
  { label: 'Elementary (6-10)', value: '6-10' },
  { label: 'Middle School (11-13)', value: '11-13' },
  { label: 'High School (14-18)', value: '14-18' },
  { label: 'Adults (18+)', value: '18+' }
]
const QuestCreateUpdate = ({ form, upload, user }) => {
  const {
    getFieldDecorator,
    validateFields,
    getFieldsValue,
    getFieldValue,
    setFieldsValue
  } = form

  const { t } = useTranslation()
  const { active_organization_id, id: user_id } = user?.info
  const [storyModalVisible, setStoryModalVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const pdfRef = useRef(null)
  const [generatedContent, setGeneratedContent] = useState([])
  const [contentSource, setContentSource] = useState('internet')
  const [isGenerating, setIsGenerating] = useState(false)
  const [users, setUsers] = useState([])
  const [formData, setFormData] = useState({
    cover_id: '',
    file_list: "",
    org_id: String(active_organization_id),
    user_id: String(user_id),
    title: '',
    age_group: '',
    objectives: "",
    num_steps: null,
    points_per_stop: null,
    max_story_words: null,
    story_preferences: null,
    editor_user_ids: []
  })
  const [selectedCover, setSelectedCover] = useState({
    id: '',
    link: '',
    file_size: '',
    filename: ''
  })
  const [attachedData, setAttachedData] = useState({
    filenames: [],
    attachmentIds: []
  })

  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false)
  const [allLocations, setAllLocations] = useState([])
  const [selectedLocationId, setSelectedLocationId] = useState(null)

  const searchParams = new URLSearchParams(window.location.search)
  const hunt_id = searchParams.get('hunt_id')

  useEffect(() => {
    getUserList()
    if (hunt_id) {
      fetchQuestData(hunt_id)
    }
  }, [hunt_id])

  const fetchQuestData = async (id) => {
    try {
      setIsFetching(true)
      const res = await api.quest.getSpecificQuest(id)
      if (res && res.data) {
        setFormData(prev => ({ ...prev, ...res.data }))

        const safeSetFields = {}
        const fieldsToSet = [
          'title', 'editor_user_ids', 'age_group', 'num_steps',
          'points_per_stop', 'max_story_words', 'cover_id',
          'objectives', 'story_preferences'
        ]

        fieldsToSet.forEach(field => {
          if (res.data[field] !== undefined) {
            safeSetFields[field] = res.data[field]
          }
        })

        setFieldsValue(safeSetFields)

        if (res.data?.cover) {
          setSelectedCover({
            id: res.data.cover.id,
            link: res.data.cover.link || "https://dev-cdn.v2.sharelookapp.com/public/" + res.data.cover.link.split("/").pop(),
            filename: res.data.cover.filename,
            file_size: res.data.cover.file_size
          })
          setFieldsValue({ cover_id: res.data.cover.id })
        }

        if (res.data.stops) {
          setGeneratedContent(res.data.stops)
        }

        if (res.data.locations) {
          setAllLocations(res.data.locations)
          if (res.data.locations.length > 0) {
            setSelectedLocationId(res.data.locations[0].id || res.data.locations[0].location_id || 0)
          } else {
            setSelectedLocationId(null)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching quest:', error)
      message.error('Failed to fetch quest details')
    } finally {
      setIsFetching(false)
    }
  }

  const handleSelectLocation = (location) => {
    if (!location) return
    setSelectedLocationId(location.id || location.location_id || 0)
    setActiveIndex(null)
  }

  const getMergedQuest = () => {
    if (!formData.title) return { stops: generatedContent }
    const activeLocation = allLocations.find(l => (l.id || l.location_id || 0) === selectedLocationId) ||
      (selectedLocationId === 0 && allLocations[0] ? allLocations[0] : null)

    if (!activeLocation) return { ...formData, stops: generatedContent }

    const existingStops = generatedContent || []
    const aiStops = activeLocation.stops || []

    const mergedStops = existingStops.map((stop, index) => {
      const aiStop = aiStops.find(s => s.stop_number === (index + 1))
      return aiStop ? { ...stop, ...aiStop } : stop
    })

    return {
      ...formData,
      ...activeLocation,
      stops: mergedStops
    }
  }

  const mergedQuest = getMergedQuest()
  const stopsToDisplay = mergedQuest.stops || []

  const syncFormData = () => {
    const values = form.getFieldsValue()
    const newFormData = { ...formData }
    Object.keys(values).forEach(key => {
      if (key === 'editor_user_ids' && Array.isArray(values[key])) {
        _.set(newFormData, key, values[key].map(String))
      } else {
        _.set(newFormData, key, values[key])
      }
    })
    newFormData.file_list = attachedData?.filenames.map(f => f.path)
    setFormData(newFormData)
    return newFormData
  }
  const navigateTo = () => {
    history.push('/quest-list')
  }
  // Load dummy generated content when moving to step 3
  const generateQuestContent = async () => {
    validateFields(async (err) => {
      if (err) return

      setIsGenerating(true)
      try {
        const currentValues = syncFormData()
        const res = await api.quest.generateQuestContent(currentValues)
        if (res && res.status === 200) {
          if (res.data && res.data.stops) {
            setGeneratedContent(res.data.stops)
            history.push(`/quest-create?hunt_id=${res.data?.hunt_id}`)
            message.success('Quest content generated successfully')
          } else {
            message.error('Unexpected response format: stops data missing')
          }
        } else {
          message.error(`Failed to generate quest: ${res.statusText || 'Unknown error'}`)
        }
      } catch (error) {
        console.error(error)
        message.error('An error occurred while generating quest content')
      } finally {
        setIsGenerating(false)
      }
    })
  }

  const handleSave = async () => {
    validateFields(async (err) => {
      if (err) return

      setIsSaving(true)
      try {
        const currentValues = syncFormData()
        const payload = {
          ...currentValues,
          stops: (generatedContent || []).map((stop, index) => ({
            ...stop,
            stop_number: stop.number || index + 1
          }))
        }
        const params = { user_id: currentValues.user_id, org_id: currentValues.org_id }
        const res = await api.quest.updateQuest(hunt_id, params, payload)
        if (res && res.status === 200) {
          message.success('Quest saved successfully')
          navigateTo()
        } else {
          message.error(`Failed to save quest: ${res.statusText || 'Unknown error'}`)
        }
      } catch (error) {
        console.error(error)
        message.error('An error occurred while saving quest')
      } finally {
        setIsSaving(false)
      }
    })
  }



  const handleDownloadPdf = async () => {
    if (!generatedContent || generatedContent.length === 0) {
      message.warning('No content available to export.')
      return
    }

    setIsExporting(true)
    const hideLoading = message.loading('Preparing PDF...', 0)

    try {
      // We'll create a temporary div for the printable content
      const printArea = document.getElementById('quest-pdf-print-area')
      if (!printArea) throw new Error('Print area not found')

      // Temporarily show it for capture
      printArea.style.display = 'block'

      const canvas = await html2canvas(printArea, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      })

      printArea.style.display = 'none'

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 10 // Margin top

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      pdf.save(`Quest_Export_${formData.title.replace(/\s+/g, '_')}.pdf`)
      message.success('Quest PDF downloaded successfully!')
    } catch (error) {
      console.error('PDF Export Error:', error)
      message.error('Failed to generate PDF. Please try again.')
    } finally {
      hideLoading()
      setIsExporting(false)
    }
  }
  const setStoryMode = newValue => {
    setFormData({
      ...formData,
      story_preferences: newValue
    })
    setFieldsValue({ story_preferences: newValue })
    setStoryModalVisible(false)
  }
  const onFiles = filesArray => {
    if (_.isEmpty(filesArray) && _.isEmpty(attachedData.attachmentIds)) return
    const filesById = filesArray.map(item => item.id)
    filesById &&
      setAttachedData({
        ...attachedData,
        filenames: filesArray,
        attachmentIds: filesById
      })
  }
  const handleDocumentSelect = (value) => {
    console.log('test document select', value)
  }
  const handleFinalSubmit = (value) => {
    if (value.length === 0) {
      setContentSource('organization')
    } else {
      setContentSource('document')
      onFiles(value)
    }
  }

  const onCoverChange = async coverObj => {
    setFieldsValue({ cover_id: coverObj.id })
    setFormData(prev => ({
      ...prev,
      cover_id: coverObj.id
    }))
  }
  const getUserList = async () => {
    try {
      const res = await api.quest.getUserList({ org_id: active_organization_id, limit: 50 })
      if (res.data) {
        setUsers(res.data?.users || [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }
  const deleteLocation = async (locationId) => {
    try {
      const res = await api.quest.deleteLocation(locationId)
      if (res.data) {
        message.success('Location deleted successfully')

        // Update local state immediately for better UX and to handle the "no locations" fallback
        setAllLocations(prev => {
          const filtered = prev.filter(l => (l.id || l.location_id) !== locationId);

          // If no locations left, or if we deleted the currently selected one, update the selection
          if (filtered.length === 0) {
            setSelectedLocationId(null);
          } else if (selectedLocationId === locationId) {
            setSelectedLocationId(filtered[0].id || filtered[0].location_id || null);
          }

          return filtered;
        });

        if (hunt_id) {
          fetchQuestData(hunt_id)
        }
      }
    } catch (error) {
      console.error('Error deleting location:', error)
    }
  }
  const goToLocation = (index) => {
    if (selectedLocationId) {
      history.push(`/quest/${hunt_id}?location_id=${selectedLocationId}&stop_index=${index}`);
    } else {
      message.error("Please add a location to view available stops.");
    }
  }

  return (
    <Spin spinning={isFetching}>
      <section className="section" style={{ paddingBottom: '10rem' }}>
        <QuestHeader t={t} type={hunt_id ? "Update" : "Create"} />
        <Row gutter={[16, 16]} justify="center" type="flex">
          <Col xs={24} sm={24} md={24}>
            <div className="form-body">
              <Form layout="vertical">
                <div className="create-course">
                  <div className="create-course__content">
                    <Row gutter={[16, 16]}>
                      <Col md={8} sm={12}>
                        <Form.Item
                          label="Title"
                          colon={false}
                          required
                        >
                          {getFieldDecorator('title', {
                            initialValue: formData.title,
                            rules: [
                              {
                                required: true,
                                message: 'Please input Quest Title!'
                              }
                            ]
                          })(<Input size="large" placeholder="Quest Title" />)}
                        </Form.Item>
                      </Col>
                      {!hunt_id && (
                        <Col md={8} sm={12}>
                          <Form.Item label="Allow Others to Edit" colon={false}>
                            {getFieldDecorator('editor_user_ids', {
                              initialValue: formData.editor_user_ids,
                            })(<Select
                              size='large'
                              mode="multiple"
                              placeholder="Select Users"
                              value={formData.editor_user_ids}
                            >
                              {users.map(user => (
                                <Option key={user.id} value={user.id}>
                                  {user.first_name + ' ' + user.last_name}
                                </Option>
                              ))}
                            </Select>)}
                          </Form.Item>
                        </Col>
                      )}
                      <Col md={8} sm={12}>
                        <Form.Item label="Age Group" required colon={false}>
                          {getFieldDecorator('age_group', {
                            initialValue: formData.age_group,
                            rules: [{ required: true, message: 'Please select Age Group!' }]
                          })(
                            <Select size="large" placeholder="Select age group">
                              {ageGroupOptions.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                      <Col md={8} sm={12}>
                        <Form.Item label="Number of stops" required colon={false}>
                          {getFieldDecorator('num_steps', {
                            initialValue: formData.num_steps,
                            rules: [
                              { required: true, message: 'Please input number of stops!' },
                              {
                                type: 'number',
                                min: 1,
                                max: 25,
                                message: 'Number of stops must be between 1 and 25'
                              }
                            ]
                          })(
                            <InputNumber
                              size="large"
                              style={{ width: '100%', borderRadius: 4 }}
                              min={1}
                              max={25}
                              disabled={!!hunt_id}
                            />
                          )}
                        </Form.Item>
                      </Col>
                      <Col md={8} sm={12}>
                        <Form.Item label="Points per stop" required colon={false}>
                          {getFieldDecorator('points_per_stop', {
                            initialValue: formData.points_per_stop,
                            rules: [
                              { required: true, message: 'Please input points per stop!' },
                              {
                                type: 'number',
                                min: 1,
                                max: 100,
                                message: 'Points per stop must be between 1 and 100'
                              }
                            ]
                          })(
                            <InputNumber
                              min={1}
                              max={100}
                              size="large"
                              style={{ width: '100%', borderRadius: 4 }}
                            />
                          )}
                        </Form.Item>
                      </Col>
                      {!hunt_id && (
                        <Col md={8} sm={12}>
                          <Form.Item label="Max Story Words" required colon={false}>
                            {getFieldDecorator('max_story_words', {
                              initialValue: formData.max_story_words,
                              rules: [{ required: true, message: 'Please input max story words!' }]
                            })(
                              <InputNumber
                                min={1}
                                size="large"
                                style={{ width: '100%', borderRadius: 4 }}
                              />
                            )}
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={14} lg={14} xl={12}>
                        <Form.Item
                          required={false}
                          colon={false}
                        >
                          {getFieldDecorator('cover_id', {
                            initialValue: formData.cover_id,
                            rules: [
                              {
                                required: true,
                                message: t('v3:required_cover_image')
                              }
                            ]
                          })(
                            <ImageSelect
                              form={form}
                              required={false}
                              label={t('v4:cover_picture')}
                              coverInput={selectedCover || null}
                              onCoverChange={onCoverChange}
                            />
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={10} lg={10} xl={12}>
                        <div style={{ marginTop: 20 }}>
                          <div style={{
                            color: "#212121",
                            textTransform: "capitalize",
                            fontWeight: "600",
                            fontSize: "14px",
                            paddingBottom: "8px"
                          }}>
                            Upload Files (word files, pdf)
                          </div>
                          <ContentChoiceStep
                            isCourse={false}
                            onSelect={handleDocumentSelect}
                            handleFinalSubmit={handleFinalSubmit}
                            isSubmitting={isGenerating}
                            onUploadingChange={setIsUploading}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col md={24}>
                        <Form.Item label="Objectives" required colon={false}>
                          {getFieldDecorator('objectives', {
                            initialValue: formData.objectives,
                            rules: [{ required: true, message: 'Please input Objectives!' }]
                          })(<Input.TextArea rows={4} />)}
                        </Form.Item>
                      </Col>
                    </Row>
                    {!hunt_id && (
                      <Col md={12}>
                        <Form.Item label="Story Preferences" required colon={false}>
                          {getFieldDecorator('story_preferences', {
                            initialValue: formData.story_preferences,
                            rules: [
                              {
                                required: true,
                                message: 'Please set your story preferences!'
                              }
                            ]
                          })(
                            <Button
                              onClick={() => setStoryModalVisible(true)}
                              type={
                                getFieldValue('story_preferences')
                                  ? 'primary'
                                  : 'default'
                              }
                              size="large"
                              style={{ width: '100%' }}
                            >
                              {getFieldValue('story_preferences') && (
                                <Icon type="check" />
                              )}{' '}
                              Set Story Mode
                            </Button>
                          )}
                        </Form.Item>
                        <StoryModal
                          form={form}
                          setStoryMode={setStoryMode}
                          visible={storyModalVisible}
                          onCancel={() => setStoryModalVisible(false)}
                        />
                      </Col>
                    )}
                  </div>

                  <div className="create-course__sidebar">
                    {allLocations.length > 0 && (
                      <div className="location-variations-section">
                        <h4 className="corporate-label" style={{
                          color: "#262626",
                          textTransform: "none",
                          fontWeight: "700",
                          fontSize: "14px",
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <Icon type="environment" style={{ marginRight: 8, color: '#1890ff' }} /> Location Variations
                        </h4>
                        <div className="variations-grid">
                          {allLocations.map((location, index) => {
                            const locId = location.location_id
                            const isActive = selectedLocationId === locId;
                            return (
                              <div
                                className={`location-variant-card ${isActive ? 'active' : ''}`}
                                key={index}
                                onClick={() => handleSelectLocation(location)}
                                style={{
                                  padding: '10px',
                                  border: isActive ? '1px solid #1890ff' : '1px solid #f0f0f0',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  background: isActive ? '#e6f7ff' : '#fff',
                                  transition: 'all 0.3s'
                                }}
                              >
                                <div className="variant-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                    <Icon type="rocket" style={{ marginRight: 8, color: isActive ? '#1890ff' : '#8c8c8c', flexShrink: 0 }} />
                                    <span style={{
                                      fontSize: '13px',
                                      fontWeight: isActive ? '600' : 'normal',
                                      color: isActive ? '#1890ff' : '#262626',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {location?.starting_location?.name || location?.starting_introduction?.name || `Variation ${index + 1}`}
                                    </span>
                                  </div>
                                  <Icon
                                    type="delete"
                                    style={{
                                      color: '#8c8c8c',
                                      marginLeft: 12,
                                      fontSize: '14px',
                                      padding: '4px',
                                      borderRadius: '4px',
                                      transition: 'all 0.3s'
                                    }}
                                    className="delete-variation-icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      Modal.confirm({
                                        title: 'Delete Variation',
                                        content: 'Are you sure you want to delete this location variation? This action cannot be undone.',
                                        okText: 'Yes, Delete',
                                        okType: 'danger',
                                        cancelText: 'Cancel',
                                        onOk: () => deleteLocation(locId),
                                        centered: true,
                                        className: 'corporate-modal'
                                      });
                                    }}
                                    onMouseEnter={(e) => { e.target.style.color = '#ff4d4f'; e.target.style.background = 'rgba(255, 77, 79, 0.1)'; }}
                                    onMouseLeave={(e) => { e.target.style.color = '#8c8c8c'; e.target.style.background = 'transparent'; }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="quest-stops-list">
                      <div className="quest-stops-list__header">
                        <span style={{ flex: 1 }}>Stops ({stopsToDisplay.length})</span>
                        <Button
                          type="primary"
                          className="rounded"
                          size="small"
                          disabled={!hunt_id}
                          onClick={() => {
                            getUserList()
                            setIsLocationModalVisible(true)
                          }}
                          style={{
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 16px',
                            fontWeight: '600',
                            boxShadow: '0 2px 6px rgba(24, 144, 255, 0.25)',
                            border: 'none',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
                          }}
                        >
                          <Icon type="environment" theme="filled" style={{ fontSize: '15px', marginRight: 8, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }} />
                          <span style={{ fontSize: '13px' }}>
                            {allLocations.length > 0 ? 'Add More Location' : 'Add Location'}
                          </span>
                        </Button>
                      </div>
                      <div className="quest-stops-list__body">
                        {stopsToDisplay.length > 0 ? (
                          stopsToDisplay.map((content, index) => (
                            <div
                              key={index}
                              className="quest-stop-item"
                              onClick={() => {
                                goToLocation(index + 1)
                              }}
                            >
                              <div className="quest-stop-item__num">
                                {content.number || index + 1}
                              </div>
                              <div className="quest-stop-item__info">
                                <div className="quest-stop-item__title">
                                  {content.title}
                                </div>
                                {content.location && (
                                  <div className="quest-stop-item__location">
                                    <Icon type="environment" style={{ marginRight: 4, fontSize: 11 }} />
                                    {content.location.name}
                                  </div>
                                )}
                              </div>
                              <Tag
                                style={{ flexShrink: 0, backgroundColor: '#f0f5ff', color: '#1d39c4', border: '1px solid #adc6ff' }}
                              >
                                {content.points || 100} pts
                              </Tag>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#bfbfbf' }}>
                            <Icon type="environment" style={{ fontSize: 24, marginBottom: 8 }} />
                            <div>No stops generated yet.</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </section >
      <div className="p-footer">
        <div className="p-footer__inner">
          {!isFetching && (
            <div className="p-footer__item">
              <Fragment>
                &nbsp;
                <span>
                  <Fragment>
                    <Button
                      type="primary"
                      className="rounded"
                      onClick={navigateTo}
                      style={{ marginRight: 8 }}
                    >
                      <Icon type="left" />
                      Back to Quest List
                    </Button>
                    <Button
                      type="primary"
                      className="rounded"
                      loading={isGenerating || isSaving}
                      disabled={isGenerating || isUploading || isSaving}
                      onClick={hunt_id ? handleSave : generateQuestContent}
                    >
                      {hunt_id ? 'Save' : 'Generate Content'}
                    </Button>
                  </Fragment>
                </span>
              </Fragment>
            </div>
          )}
        </div>
      </div>

      <GenerateLocationModal
        visible={isLocationModalVisible}
        onCancel={() => setIsLocationModalVisible(false)}
        huntId={hunt_id}
        users={users}
        userId={user_id}
        onSuccess={(newData) => {
          setAllLocations(prev => [...prev, newData])
          setSelectedLocationId(newData.id || newData.location_id)
          if (!generatedContent || generatedContent.length === 0) {
            if (newData.stops && newData.stops.length > 0) {
              setGeneratedContent(newData.stops)
            }
          }
        }}
      />

      {/* Hidden container for PDF generation */}
      <div id="quest-pdf-print-area" style={{
        display: 'none',
        width: '800px',
        padding: '40px',
        background: '#fff',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial'
      }}>
        <div style={{ borderBottom: '2px solid #1890ff', paddingBottom: '10px', marginBottom: '20px' }}>
          <h1 style={{ color: '#000', margin: 0, fontSize: '24px' }}>{formData.title}</h1>
          <p style={{ color: '#595959', margin: '5px 0 0', fontSize: '13px' }}>
            Age Group: {formData.age_group} | Genre: {formData.story_preferences?.genre} | Tone: {formData.story_preferences?.tone}
          </p>
        </div>

        {generatedContent.map((content, index) => (
          <div key={index} style={{ marginBottom: '25px', borderBottom: '1px solid #f0f0f0', paddingBottom: '20px' }}>
            <div style={{
              background: '#f0f5ff',
              padding: '10px 15px',
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h2 style={{ margin: 0, fontSize: '16px', color: '#1d39c4' }}>
                #{content.number || index + 1} {content.title}
              </h2>
              <span style={{ fontWeight: 'bold' }}>{content.points || 100} PTS</span>
            </div>

            <div style={{ padding: '0 5px' }}>
              <h4 style={{ color: '#8c8c8c', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' }}>Mission Narrative</h4>
              <p style={{ fontSize: '13px', lineHeight: '1.5', marginBottom: '16px', color: '#262626' }}>{content.story}</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '16px' }}>
                <div>
                  <h4 style={{ color: '#8c8c8c', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' }}>Fun Fact</h4>
                  <div style={{ background: '#fffbe6', padding: '8px 12px', borderRadius: '4px', fontSize: '12px', color: '#856404', border: '1px solid #ffe58f' }}>
                    {content.fun_fact}
                  </div>
                </div>
                <div>
                  <h4 style={{ color: '#8c8c8c', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' }}>Location Hints</h4>
                  <div style={{ background: '#f0f7ff', padding: '8px 12px', borderRadius: '4px', fontSize: '12px', color: '#003a8c', border: '1px solid #bae7ff' }}>
                    <strong>Atmosphere:</strong> {content.location_hints?.atmosphere}<br />
                    <strong>Requirements:</strong> {content.location_hints?.requirements}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <h4 style={{ color: '#8c8c8c', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' }}>Knowledge Check</h4>
                  <div style={{ border: '1px solid #e8e8e8', borderRadius: '4px', padding: '8px 12px' }}>
                    <p style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '6px', color: '#262626' }}>{content.quiz?.multiple_choice?.question}</p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '11px' }}>
                      {content.quiz?.multiple_choice?.options?.map((opt, i) => (
                        <li key={i} style={{
                          padding: '2px 0',
                          color: i === content.quiz?.multiple_choice?.correct_index ? '#237804' : '#595959',
                          fontWeight: i === content.quiz?.multiple_choice?.correct_index ? 'bold' : 'normal'
                        }}>
                          • {opt} {i === content.quiz?.multiple_choice?.correct_index ? '(Correct)' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>
                  <h4 style={{ color: '#8c8c8c', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' }}>Strategic Inquiry</h4>
                  <div style={{ fontSize: '12px', color: '#595959', backgroundColor: '#fafafa', padding: '8px 12px', borderRadius: '4px', fontStyle: 'italic', borderLeft: '3px solid #d9d9d9' }}>
                    {content.quiz?.open_ended}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '10px', color: '#bfbfbf' }}>
          Generated by Sharelook Quest AI
        </div>
      </div>
    </Spin >
  )
}
const mapStateToProps = state => ({
  organizations: state.organizations.list,
  user: state.user
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      upload: (file, opts) => dispatch(actions.library.upload(file, opts)),
      updateTopicList: actions.topics.updateTopicList
    },
    dispatch
  )
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Form.create({ name: 'quest_form' })(QuestCreateUpdate))

